import {
  CheckCircle,
  Circle,
  Clock,
  ExternalLink,
  ShieldOff,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  RuleEvaluationSummarySchema,
  SlaStatus,
} from '@/services/api/generated/externalTaskService';

import { formatDate } from './dateUtils';

interface ExternalTaskMatchStatusProps {
  evaluations: RuleEvaluationSummarySchema[];
  isExcluded: boolean;
  excludedByRuleName?: string | null;
  excludedByRuleId?: string | null;
  isExclusionPending?: boolean;
  pendingExclusionRuleName?: string | null;
  pendingExclusionRuleId?: string | null;
  firmId: string;
}

/**
 * Displays rule evaluation status for an external task.
 * Shows whether the task has been matched by linking rules or excluded.
 */
export const ExternalTaskMatchStatus: React.FC<
  ExternalTaskMatchStatusProps
> = ({
  evaluations,
  isExcluded,
  excludedByRuleName,
  excludedByRuleId,
  isExclusionPending,
  pendingExclusionRuleName,
  pendingExclusionRuleId,
  firmId,
}) => {
  // If task is excluded, show prominent exclusion banner
  if (isExcluded) {
    return (
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
        <div className="flex items-center gap-2">
          <ShieldOff className="h-4 w-4 text-orange-600" />
          <span className="font-medium text-orange-700">
            Excluded from automatic linking
          </span>
        </div>
        {excludedByRuleName && (
          <p className="mt-1 text-sm text-orange-600">
            By rule:{' '}
            {excludedByRuleId ? (
              <Link
                to={`/firms/${firmId}/task-linking/${excludedByRuleId}`}
                className="underline hover:text-orange-700 inline-flex items-center gap-1"
              >
                {excludedByRuleName}
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              excludedByRuleName
            )}
          </p>
        )}
      </div>
    );
  }

  const isPending = isExclusionPending ?? false;

  if (isPending && evaluations.length === 0) {
    return (
      <PendingReconciliationBanner
        firmId={firmId}
        pendingExclusionRuleId={pendingExclusionRuleId}
        pendingExclusionRuleName={pendingExclusionRuleName}
      />
    );
  }

  // No evaluations yet
  if (evaluations.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No linking rules have evaluated this task yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {isPending && (
        <PendingReconciliationBanner
          firmId={firmId}
          pendingExclusionRuleId={pendingExclusionRuleId}
          pendingExclusionRuleName={pendingExclusionRuleName}
        />
      )}
      {evaluations.map((evaluation) => (
        <RuleEvaluationRow
          key={evaluation.rule_id}
          evaluation={evaluation}
          firmId={firmId}
        />
      ))}
    </div>
  );
};

const PendingReconciliationBanner: React.FC<{
  firmId: string;
  pendingExclusionRuleId?: string | null;
  pendingExclusionRuleName?: string | null;
}> = ({ firmId, pendingExclusionRuleId, pendingExclusionRuleName }) => {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-amber-600" />
        <span className="font-medium text-amber-800">
          Automatic linking status updating
        </span>
      </div>
      <p className="mt-1 text-sm text-amber-700">
        Rule change is being reconciled.
      </p>
      {pendingExclusionRuleName && (
        <p className="mt-1 text-sm text-amber-700">
          Pending rule:{' '}
          {pendingExclusionRuleId ? (
            <Link
              to={`/firms/${firmId}/task-linking/${pendingExclusionRuleId}`}
              className="underline hover:text-amber-800 inline-flex items-center gap-1"
            >
              {pendingExclusionRuleName}
              <ExternalLink className="h-3 w-3" />
            </Link>
          ) : (
            pendingExclusionRuleName
          )}
        </p>
      )}
    </div>
  );
};

/**
 * Individual row showing a single rule evaluation result.
 */
const RuleEvaluationRow: React.FC<{
  evaluation: RuleEvaluationSummarySchema;
  firmId: string;
}> = ({ evaluation, firmId }) => {
  const isMatched = evaluation.evaluation_status === 'MATCHED';
  const hasLinks = evaluation.links_created > 0;

  return (
    <div className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="flex items-center gap-2">
        {/* Match status icon */}
        {isMatched ? (
          <CheckCircle className="h-4 w-4 text-green-600" />
        ) : (
          <Circle className="h-4 w-4 text-slate-400" />
        )}

        {/* Rule name with link */}
        <Link
          to={`/firms/${firmId}/task-linking/${evaluation.rule_id}`}
          className="font-medium text-slate-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1"
        >
          {evaluation.rule_name}
          <ExternalLink className="h-3 w-3 text-slate-400" />
        </Link>
      </div>

      <div className="flex items-center gap-3 text-sm">
        {/* Match result */}
        {isMatched && hasLinks && (
          <span className="text-green-600">
            {evaluation.links_created} link
            {evaluation.links_created !== 1 ? 's' : ''} created
          </span>
        )}
        {isMatched && !hasLinks && (
          <span className="text-slate-500">Matched, no new links</span>
        )}
        {!isMatched && <span className="text-slate-400">No match</span>}

        {/* SLA indicator */}
        {evaluation.match_sla && evaluation.sla_status && (
          <SLAIndicator
            status={evaluation.sla_status}
            matchSla={evaluation.match_sla}
            evaluatedAt={evaluation.evaluated_at}
          />
        )}
      </div>
    </div>
  );
};

/**
 * SLA status indicator with tooltip showing details.
 */
const SLAIndicator: React.FC<{
  status: SlaStatus;
  matchSla: string;
  evaluatedAt: string;
}> = ({ status, matchSla, evaluatedAt }) => {
  if (status === 'no_sla') return null;

  const getStatusDisplay = () => {
    switch (status) {
      case 'on_time':
        return {
          icon: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
          label: 'On time',
          className: 'text-emerald-600',
        };
      case 'late':
        return {
          icon: <Clock className="h-3.5 w-3.5 text-red-600" />,
          label: 'SLA breached',
          className: 'text-red-600',
        };
      case 'pending':
        return {
          icon: <Clock className="h-3.5 w-3.5 text-amber-600" />,
          label: 'Pending',
          className: 'text-amber-600',
        };
      default:
        return null;
    }
  };

  const display = getStatusDisplay();
  if (!display) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="flex items-center gap-1">
          {display.icon}
          <span className={`text-xs ${display.className}`}>
            {display.label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Target: {formatDuration(matchSla)} after match</p>
          <p>Matched: {formatDate(evaluatedAt)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Parse ISO 8601 duration (e.g., "PT24H") and format for display.
 */
function formatDuration(iso: string): string {
  const match = iso.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return iso;

  const [, days, hours, minutes] = match;
  const d = parseInt(days || '0', 10);
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);

  const parts: string[] = [];
  if (d > 0) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} min`);

  return parts.length > 0 ? parts.join(' ') : '0 min';
}
